/**
 * Conquest Access Panel
 * Master Admin control for a reseller's Conquest Audit sub-account roles
 * (Admin / User / Viewer) plus cross-reseller Viewer grants.
 * Distinct from ResellerTeamPanel — this is Conquest-Audit-scoped access,
 * not the reseller's general platform team.
 */
import { useState, useEffect, useCallback } from "react";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserPlus, ShieldCheck, User, Eye, Trash2, ArrowLeftRight } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", icon: ShieldCheck, cls: "bg-indigo-100 text-indigo-800" },
  user: { label: "User", icon: User, cls: "bg-blue-100 text-blue-800" },
  viewer: { label: "Viewer", icon: Eye, cls: "bg-gray-100 text-gray-700" },
};

const formatDate = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const ConquestAccessPanel = ({ resellerId, resellerName }) => {
  const { apiFetch } = useAuth();
  const { success: showToast, error: showError } = useToast();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "user" });
  const [inviting, setInviting] = useState(false);
  const [showCrossGrantModal, setShowCrossGrantModal] = useState(false);
  const [crossGrantEmail, setCrossGrantEmail] = useState("");
  const [grantingCross, setGrantingCross] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const fetchUsers = useCallback(async () => {
    if (!resellerId) return;
    try {
      setLoading(true);
      const response = await apiFetch(`/admin/resellers/${resellerId}/conquest/users`);
      setUsers(response.data?.users || []);
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to load Conquest Audit access list");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, resellerId, showError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleInvite = async () => {
    const email = inviteForm.email.trim().toLowerCase();
    if (!email) {
      showError("Email is required");
      return;
    }

    // Inviting someone as Admin atomically demotes the reseller's current
    // Admin to User (Stage 2's transfer behavior) — surface that
    // consequence before it happens, not silently behind a dropdown.
    if (inviteForm.role === "admin") {
      const currentAdmin = users.find((u) => !u.is_cross_grant && u.role === "admin");
      if (currentAdmin && currentAdmin.email.toLowerCase() !== email) {
        const confirmed = window.confirm(
          `${currentAdmin.email} is currently this reseller's Admin. Granting Admin to ${email} will ` +
          `automatically demote ${currentAdmin.email} to User. Continue?`
        );
        if (!confirmed) return;
      }
    }

    try {
      setInviting(true);
      const result = await apiFetch(`/admin/resellers/${resellerId}/conquest/users/invite`, {
        method: "POST",
        data: { email, role: inviteForm.role },
      });
      if (result.data?.forced_admin) {
        showToast(`${email} granted access — auto-assigned Admin as this reseller's first Conquest user`);
      } else {
        showToast(`${email} granted Conquest Audit ${inviteForm.role} access`);
      }
      setShowInviteModal(false);
      setInviteForm({ email: "", role: "user" });
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to grant Conquest Audit access");
    } finally {
      setInviting(false);
    }
  };

  const handleCrossGrant = async () => {
    const email = crossGrantEmail.trim().toLowerCase();
    if (!email) {
      showError("Email is required");
      return;
    }
    try {
      setGrantingCross(true);
      await apiFetch(`/admin/resellers/${resellerId}/conquest/cross-grants`, {
        method: "POST",
        data: { email },
      });
      showToast(`${email} granted cross-reseller Viewer access to ${resellerName}`);
      setShowCrossGrantModal(false);
      setCrossGrantEmail("");
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to create cross-reseller grant");
    } finally {
      setGrantingCross(false);
    }
  };

  const handleRevoke = async (u) => {
    const scope = u.is_cross_grant ? "cross-reseller Viewer grant" : "Conquest Audit access";
    if (!window.confirm(`Revoke ${u.email}'s ${scope} for ${resellerName}?`)) return;
    try {
      setRevoking(u.user_id);
      await apiFetch(`/admin/resellers/${resellerId}/conquest/users/${u.user_id}/revoke`, { method: "POST" });
      showToast(`Revoked ${u.email}'s ${scope}`);
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.detail || "Failed to revoke access");
    } finally {
      setRevoking(null);
    }
  };

  const homeUsers = users.filter((u) => !u.is_cross_grant);
  const crossGrantUsers = users.filter((u) => u.is_cross_grant);

  const renderRow = (u) => {
    const meta = ROLE_META[u.role] || ROLE_META.viewer;
    const Icon = meta.icon;
    return (
      <tr key={u.id} data-testid={`conquest-access-row-${u.user_id}`}>
        <td className="py-2.5 text-gray-900">
          {u.name || "—"}
          <span className="block text-xs text-gray-500">{u.email}</span>
        </td>
        <td className="py-2.5">
          <Badge className={meta.cls} data-testid={`conquest-access-role-${u.user_id}`}>
            <Icon className="w-3 h-3 mr-1 inline" />
            {meta.label}
          </Badge>
          {u.forced_admin && (
            <span className="block text-[10px] text-gray-400 mt-0.5">auto-assigned (first invite)</span>
          )}
        </td>
        <td className="py-2.5">
          {u.is_cross_grant ? (
            <Badge variant="outline" className="text-purple-700 border-purple-300 bg-purple-50" data-testid={`conquest-access-grant-type-${u.user_id}`}>
              <ArrowLeftRight className="w-3 h-3 mr-1 inline" /> Cross-Reseller
            </Badge>
          ) : (
            <Badge variant="outline" className="text-gray-600" data-testid={`conquest-access-grant-type-${u.user_id}`}>
              Home Team
            </Badge>
          )}
        </td>
        <td className="py-2.5 text-xs text-gray-500">{u.granted_by_email || "—"}</td>
        <td className="py-2.5 text-xs text-gray-500">{formatDate(u.created_at)}</td>
        <td className="py-2.5 text-right">
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => handleRevoke(u)}
            disabled={revoking === u.user_id}
            data-testid={`revoke-conquest-access-${u.user_id}`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {revoking === u.user_id ? "Revoking…" : "Revoke"}
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <Card data-testid="conquest-access-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Conquest Audit Access</CardTitle>
            <CardDescription>
              Who can view or run Conquest Audits for {resellerName}. Separate from the general team above.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowCrossGrantModal(true)} data-testid="grant-cross-reseller-access-btn">
              <ArrowLeftRight className="w-4 h-4 mr-2" /> Grant Cross-Reseller Viewer
            </Button>
            <Button onClick={() => setShowInviteModal(true)} data-testid="invite-conquest-user-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Invite
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2" data-testid="conquest-access-loading-skeleton">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center" data-testid="no-conquest-access-message">
            No one has Conquest Audit access for this reseller yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="conquest-access-table">
              <thead>
                <tr className="border-b text-left text-xs text-gray-500">
                  <th className="pb-2 font-medium">Person</th>
                  <th className="pb-2 font-medium">Role</th>
                  <th className="pb-2 font-medium">Grant Type</th>
                  <th className="pb-2 font-medium">Granted By</th>
                  <th className="pb-2 font-medium">Since</th>
                  <th className="pb-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {homeUsers.map(renderRow)}
                {crossGrantUsers.map(renderRow)}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Invite Modal — home reseller, any role */}
      <Dialog open={showInviteModal} onOpenChange={(open) => { if (!open) setShowInviteModal(false); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="invite-conquest-user-modal">
          <DialogHeader>
            <DialogTitle>Grant Conquest Audit Access</DialogTitle>
            <DialogDescription>Person must already have an account at {resellerName}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="conquest_invite_email">Email *</Label>
              <Input
                id="conquest_invite_email"
                type="email"
                placeholder="teammate@example.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="conquest-invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="conquest_invite_role">Role *</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger id="conquest_invite_role" data-testid="conquest-invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full access, becomes the reseller's only Admin</SelectItem>
                  <SelectItem value="user">User — can run audits, no billing/team management</SelectItem>
                  <SelectItem value="viewer">Viewer — read-only, no audit runs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)} disabled={inviting}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting} data-testid="submit-invite-conquest-user">
              {inviting ? "Granting…" : "Grant Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cross-Reseller Grant Modal — Viewer-only, locked, no role choice at all */}
      <Dialog open={showCrossGrantModal} onOpenChange={(open) => { if (!open) setShowCrossGrantModal(false); }}>
        <DialogContent className="sm:max-w-[420px]" data-testid="cross-grant-modal">
          <DialogHeader>
            <DialogTitle>Grant Cross-Reseller Viewer Access</DialogTitle>
            <DialogDescription>
              Always Viewer-only — this endpoint cannot grant Admin or User rights to another reseller's data.
              Recipient must already have a home Conquest Audit account somewhere.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="cross_grant_email">Email *</Label>
              <Input
                id="cross_grant_email"
                type="email"
                placeholder="person@otherreseller.com"
                value={crossGrantEmail}
                onChange={(e) => setCrossGrantEmail(e.target.value)}
                data-testid="cross-grant-email-input"
              />
            </div>
            <div className="px-3 py-2 rounded-lg bg-purple-50 border border-purple-200 text-xs text-purple-800">
              Role is fixed to <strong>Viewer</strong> — there is no dropdown here on purpose. The database
              also enforces this (a cross-grant row can never be saved as Admin or User).
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCrossGrantModal(false)} disabled={grantingCross}>Cancel</Button>
            <Button onClick={handleCrossGrant} disabled={grantingCross} data-testid="submit-cross-grant">
              {grantingCross ? "Granting…" : "Grant Viewer Access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ConquestAccessPanel;
